import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { toast } from 'react-hot-toast';

// Types for commission model assignments
export interface CommissionModelAssignment {
  id: string;
  name: string;
  assigned_models: Array<{
    model_id: string;
    monthly_amounts: Record<string, number>; // Key format: "YYYY-MM", value: amount
  }>;
  created_at: string;
  updated_at: string;
  created_by: string;
  last_modified_by?: string;
}

export interface CreateCommissionAssignmentData {
  name: string;
  assigned_models: Array<{
    model_id: string;
    monthly_amounts: Record<string, number>;
  }>;
  created_by: string;
}

export interface UpdateCommissionAssignmentData {
  id: string;
  name?: string;
  assigned_models?: Array<{
    model_id: string;
    monthly_amounts: Record<string, number>;
  }>;
  last_modified_by: string;
}

export interface MonthlyAmountData {
  month: number;
  year: number;
  amount: number;
}

const SupabaseCommissionModelAssignmentService = {
  /**
   * Create a new commission assignment
   */
  async createCommissionAssignment(data: CreateCommissionAssignmentData): Promise<string> {
    try {
      const { data: result, error } = await supabase
        .from('commission_model_assignments')
        .insert({
          name: data.name,
          assigned_models: data.assigned_models,
          created_by: data.created_by
        })
        .select('id')
        .single();

      if (error) throw error;
      return result.id;
    } catch (error) {
      console.error('Error creating commission assignment:', error);
      throw error;
    }
  },

  /**
   * Get all commission assignments
   */
  async getCommissionAssignments(): Promise<CommissionModelAssignment[]> {
    try {
      const { data, error } = await supabase
        .from('commission_model_assignments')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data.map(item => ({
        id: item.id,
        name: item.name,
        assigned_models: item.assigned_models,
        created_at: item.created_at,
        updated_at: item.updated_at,
        created_by: item.created_by,
        last_modified_by: item.last_modified_by
      }));
    } catch (error) {
      console.error('Error getting commission assignments:', error);
      throw error;
    }
  },

  /**
   * Update a commission assignment
   */
  async updateCommissionAssignment(data: UpdateCommissionAssignmentData): Promise<void> {
    try {
      const updateData: any = {
        last_modified_by: data.last_modified_by
      };

      if (data.name !== undefined) {
        updateData.name = data.name;
      }

      if (data.assigned_models !== undefined) {
        updateData.assigned_models = data.assigned_models;
      }

      const { error } = await supabase
        .from('commission_model_assignments')
        .update(updateData)
        .eq('id', data.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating commission assignment:', error);
      throw error;
    }
  },

  /**
   * Update monthly amounts for a specific assignment
   */
  async updateMonthlyAmounts(
    assignment_id: string, 
    monthly_amounts: Record<string, number>,
    last_modified_by: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('commission_model_assignments')
        .update({
          monthly_amounts: monthly_amounts,
          last_modified_by: last_modified_by
        })
        .eq('id', assignment_id);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating monthly amounts:', error);
      throw error;
    }
  },

  /**
   * Get total amounts for a specific month across all assignments
   */
  async getTotalAmountsForMonth(month: number, year: number): Promise<number> {
    try {
      const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
      const assignments = await this.getCommissionAssignments();
      
      let total_amount = 0;
      assignments.forEach(assignment => {
        assignment.assigned_models.forEach(model => {
          if (model.monthly_amounts && model.monthly_amounts[monthKey]) {
            total_amount += model.monthly_amounts[monthKey];
          }
        });
      });
      
      return total_amount;
    } catch (error) {
      console.error('Error getting total amounts for month:', error);
      throw error;
    }
  },

  /**
   * Delete a commission assignment
   */
  async deleteCommissionAssignment(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('commission_model_assignments')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting commission assignment:', error);
      throw error;
    }
  },

  /**
   * Save all commission assignments (batch operation)
   */
  async saveAllCommissionAssignments(
    assignments: Array<{
      id?: string;
      name: string;
      assigned_models: Array<{
        model_id: string;
        monthly_amounts: Record<string, number>;
      }>;
    }>,
    user_id: string
  ): Promise<void> {
    try {
      // Get existing assignments to determine which to update vs create
      const existingAssignments = await this.getCommissionAssignments();
      const existingIds = new Set(existingAssignments.map(a => a.id));

      const operations = [];

      for (const assignment of assignments) {
        if (assignment.id && existingIds.has(assignment.id)) {
          // Update existing assignment
          operations.push(
            supabase
              .from('commission_model_assignments')
              .update({
                name: assignment.name,
                assigned_models: assignment.assigned_models,
                last_modified_by: user_id
              })
              .eq('id', assignment.id)
          );
        } else {
          // Create new assignment
          operations.push(
            supabase
              .from('commission_model_assignments')
              .insert({
                name: assignment.name,
                assigned_models: assignment.assigned_models,
                created_by: user_id
              })
          );
        }
      }

      // Execute all operations
      const results = await Promise.all(operations);
      
      // Check for errors
      for (const result of results) {
        if (result.error) {
          throw result.error;
        }
      }
    } catch (error) {
      console.error('Error saving commission assignments:', error);
      throw error;
    }
  }
};

// React Query hooks
export function useCommissionAssignments() {
  return useQuery({
    queryKey: ['supabase-commission-assignments'],
    queryFn: () => SupabaseCommissionModelAssignmentService.getCommissionAssignments(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useCreateCommissionAssignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateCommissionAssignmentData) => 
      SupabaseCommissionModelAssignmentService.createCommissionAssignment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-commission-assignments'] });
      toast.success('Commission assignment created successfully');
    },
    onError: (error) => {
      console.error('Error creating commission assignment:', error);
      toast.error('Failed to create commission assignment');
    },
  });
}

export function useUpdateCommissionAssignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: UpdateCommissionAssignmentData) => 
      SupabaseCommissionModelAssignmentService.updateCommissionAssignment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-commission-assignments'] });
      toast.success('Commission assignment updated successfully');
    },
    onError: (error) => {
      console.error('Error updating commission assignment:', error);
      toast.error('Failed to update commission assignment');
    },
  });
}

export function useDeleteCommissionAssignment() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => SupabaseCommissionModelAssignmentService.deleteCommissionAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-commission-assignments'] });
      toast.success('Commission assignment deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting commission assignment:', error);
      toast.error('Failed to delete commission assignment');
    },
  });
}

export function useSaveAllCommissionAssignments() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ assignments, user_id }: { assignments: Array<{ id?: string; name: string; assigned_models: Array<{ model_id: string; monthly_amounts: Record<string, number> }> }>; user_id: string }) => 
      SupabaseCommissionModelAssignmentService.saveAllCommissionAssignments(assignments, user_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supabase-commission-assignments'] });
      toast.success('Commission assignments saved successfully');
    },
    onError: (error) => {
      console.error('Error saving commission assignments:', error);
      toast.error('Failed to save commission assignments');
    },
  });
}

export function useGetTotalAmountsForMonth() {
  return useMutation({
    mutationFn: ({ month, year }: { month: number; year: number }) => 
      SupabaseCommissionModelAssignmentService.getTotalAmountsForMonth(month, year),
  });
}

export default SupabaseCommissionModelAssignmentService;
